import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Trophy, Medal, Search } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const FLAG_URL = (code) => `https://flagcdn.com/w40/${code?.toLowerCase()}.png`;

export default function Rankings() {
  const { user } = useAuth();
  const [globalRanking, setGlobalRanking] = useState([]);
  const [stageRanking, setStageRanking] = useState([]);
  const [groupStageRanking, setGroupStageRanking] = useState([]);
  const [stages, setStages] = useState([]);
  const [selectedStage, setSelectedStage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  // History modal state
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyContext, setHistoryContext] = useState({ user: '', scopeLabel: '' });

  const isAdmin = user?.role === 'admin';

  const openHistory = async (userId, scope, scopeLabel, stageIdParam) => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryError('');
    setHistoryData(null);
    setHistoryContext({ user: userId, scopeLabel });
    try {
      const params = new URLSearchParams({ scope });
      if (scope === 'stage' && stageIdParam) params.append('stage_id', stageIdParam);
      const res = await api.get(`/ranking/user-history/${encodeURIComponent(userId)}?${params.toString()}`);
      setHistoryData(res.data);
    } catch (err) {
      setHistoryError(err.response?.data?.detail || 'No se pudo cargar el historial');
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([
      api.get('/ranking/global'),
      api.get('/ranking/group-stage'),
      api.get('/stages'),
    ]).then(([r, g, s]) => {
      setGlobalRanking(r.data);
      setGroupStageRanking(g.data);
      setStages(s.data);
    }).finally(() => setLoading(false));
  }, []);

  const loadStageRanking = async (stageId) => {
    setSelectedStage(stageId);
    if (!stageId) return;
    try {
      const res = await api.get(`/ranking/stage/${stageId}`);
      setStageRanking(res.data);
    } catch { setStageRanking([]); }
  };

  const positionIcon = (pos) => {
    if (pos === 1) return <Trophy size={18} className="text-[#F4E39E]" />;
    if (pos === 2) return <Medal size={18} className="text-gray-300" />;
    if (pos === 3) return <Medal size={18} className="text-amber-600" />;
    return <span className="text-white/50 text-sm w-[18px] text-center">{pos}</span>;
  };

  const RankingTable = ({ data, scope, scopeLabel, stageIdParam }) => {
    // Filtrar por búsqueda (solo admin)
    let filteredData = data;
    if (isAdmin && searchTerm) {
      filteredData = data.filter(r => 
        r.user_id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Encontrar posición del usuario actual (solo para users)
    const currentUserData = !isAdmin ? filteredData.find(r => r.user_id === user?.user_id) : null;
    const otherUsers = !isAdmin && currentUserData 
      ? filteredData.filter(r => r.user_id !== user?.user_id) 
      : filteredData;

    const getMedalIcon = (pos) => {
      if (pos === 1) return '🥇';
      if (pos === 2) return '🥈';
      if (pos === 3) return '🥉';
      return null;
    };

    return (
      <div className="space-y-2">
        {/* Buscador para Admin */}
        {isAdmin && (
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#F4E39E]/50" size={20} />
              <input
                type="text"
                placeholder="Buscar usuario por ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-[#0A0F1C] border border-[#F4E39E]/30 rounded-lg pl-10 pr-4 py-3 text-white focus:border-[#F4E39E] focus:ring-1 focus:ring-[#F4E39E] outline-none"
                data-testid="ranking-search-input"
              />
            </div>
          </div>
        )}

        {/* Header de columnas */}
        <div className="glass-card p-3 mb-3" style={{ fontFamily: 'Montserrat' }}>
          <div className="grid grid-cols-12 gap-2 text-[#F4E39E] font-bold text-sm">
            <div className="col-span-2 lg:col-span-1">Pos.</div>
            <div className="col-span-4 lg:col-span-5">{!isAdmin ? 'Tu Posición' : 'Usuario'}</div>
            <div className="col-span-3 lg:col-span-3 text-center">Puntos</div>
            <div className="col-span-3 lg:col-span-3 text-right">Detalles</div>
          </div>
        </div>

        {/* Posición del usuario actual (solo para users) */}
        {!isAdmin && currentUserData && (
          <>
            <button
              type="button"
              onClick={() => openHistory(currentUserData.user_id, scope, scopeLabel, stageIdParam)}
              className="w-full text-left glass-card p-3 bg-gradient-to-r from-[#F4E39E]/20 to-[#926F39]/20 border-2 border-[#F4E39E] animate-pulse-slow cursor-pointer hover:bg-[#F4E39E]/10 transition-colors"
              data-testid="current-user-position"
            >
              <div className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-2 lg:col-span-1 flex items-center justify-center">
                  <div className="w-10 h-10 rounded-full bg-[#F4E39E] flex items-center justify-center text-black font-black text-lg" style={{ fontFamily: 'Montserrat' }}>
                    {currentUserData.position}
                  </div>
                </div>
                <div className="col-span-4 lg:col-span-5">
                  <span className="text-white font-bold text-base">{currentUserData.user_id}</span>
                  <p className="text-[#F4E39E] text-xs font-semibold">Tu posición actual · Ver historial</p>
                </div>
                <div className="col-span-3 lg:col-span-3 text-center">
                  <span className={`font-black text-2xl ${currentUserData.points === 'NJ' ? 'text-white/30' : 'text-[#F4E39E]'}`} style={{ fontFamily: 'Montserrat' }}>
                    {currentUserData.points === 'NJ' ? 'NJ' : currentUserData.points}
                  </span>
                  <span className="text-white/50 text-xs ml-1">pts</span>
                </div>
                <div className="col-span-3 lg:col-span-3 text-right">
                  {getMedalIcon(currentUserData.position) && (
                    <span className="text-3xl">{getMedalIcon(currentUserData.position)}</span>
                  )}
                </div>
              </div>
            </button>
            <div className="border-t border-[#F4E39E]/20 my-3"></div>
          </>
        )}

        {/* Lista de rankings */}
        {otherUsers.length === 0 ? (
          <div className="glass-card p-8 text-center text-white/40">
            {isAdmin && searchTerm ? 'No se encontraron usuarios' : 'No hay datos de ranking'}
          </div>
        ) : (
          otherUsers.map((r, i) => (
            <button
              key={i}
              type="button"
              onClick={() => openHistory(r.user_id, scope, scopeLabel, stageIdParam)}
              className={`w-full text-left glass-card p-3 transition-all hover:border-[#F4E39E]/50 hover:bg-[#F4E39E]/5 cursor-pointer ${
                r.position <= 3 ? 'border-[#F4E39E]/40' : ''
              }`}
              data-testid={`ranking-row-${i}`}
            >
              <div className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-2 lg:col-span-1 flex items-center justify-center">
                  <div className="w-10 h-10 rounded-full bg-[#0A0F1C] flex items-center justify-center">
                    {r.position <= 3 ? (
                      <span className="text-2xl">{positionIcon(r.position)}</span>
                    ) : (
                      <span className="text-white/50 font-bold text-sm" style={{ fontFamily: 'Montserrat' }}>{r.position}</span>
                    )}
                  </div>
                </div>
                <div className="col-span-4 lg:col-span-5">
                  <span className="text-white font-semibold text-sm">{r.user_id}</span>
                </div>
                <div className="col-span-3 lg:col-span-3 text-center">
                  <span className={`font-bold text-lg ${r.points === 'NJ' ? 'text-white/30' : 'text-[#F4E39E]'}`} style={{ fontFamily: 'Montserrat' }}>
                    {r.points === 'NJ' ? 'NJ' : r.points}
                  </span>
                  <span className="text-white/30 text-xs ml-1">pts</span>
                </div>
                <div className="col-span-3 lg:col-span-3 text-right flex items-center justify-end gap-2">
                  {getMedalIcon(r.position) && (
                    <span className="text-2xl">{getMedalIcon(r.position)}</span>
                  )}
                  <span className="text-[#F4E39E]/50 text-sm">›</span>
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="rankings-page">
      <h2 className="text-2xl lg:text-3xl font-extrabold gold-text" style={{ fontFamily: 'Montserrat' }}>Ranking</h2>

      <Tabs defaultValue="global" className="w-full">
        <TabsList className="w-full lg:w-auto bg-[#111A30] border border-[#F4E39E]/20">
          <TabsTrigger value="global" className="flex-1 gold-tab text-white/60" data-testid="tab-global">
            Global
          </TabsTrigger>
          <TabsTrigger value="group" className="flex-1 gold-tab text-white/60" data-testid="tab-group">
            Fase Grupos
          </TabsTrigger>
          <TabsTrigger value="stage" className="flex-1 gold-tab text-white/60" data-testid="tab-stage">
            Por Etapa
          </TabsTrigger>
        </TabsList>

        <TabsContent value="global" className="mt-4">
          {loading ? (
            <div className="text-center text-white/50 py-10">Cargando...</div>
          ) : (
            <RankingTable data={globalRanking} scope="global" scopeLabel="Global" />
          )}
        </TabsContent>

        <TabsContent value="group" className="mt-4">
          <div className="mb-4 glass-card p-4 border-l-4 border-[#F4E39E]">
            <p className="text-white/70 text-sm">
              <strong className="text-[#F4E39E]">Fase de Grupos:</strong> Ranking acumulado de las primeras 3 fases del torneo
            </p>
          </div>
          {loading ? (
            <div className="text-center text-white/50 py-10">Cargando...</div>
          ) : (
            <RankingTable data={groupStageRanking} scope="group-stage" scopeLabel="Fase de Grupos" />
          )}
        </TabsContent>

        <TabsContent value="stage" className="mt-4 space-y-4">
          <Select value={selectedStage} onValueChange={loadStageRanking}>
            <SelectTrigger className="bg-[#111A30] border-[#F4E39E]/30 text-white" data-testid="ranking-stage-select">
              <SelectValue placeholder="Elegir Etapa" />
            </SelectTrigger>
            <SelectContent className="bg-[#111A30] border-[#F4E39E]/30">
              {stages.map(s => (
                <SelectItem key={s.id} value={s.id} className="text-white">{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <RankingTable
            data={stageRanking}
            scope="stage"
            scopeLabel={stages.find(s => s.id === selectedStage)?.name || 'Por Etapa'}
            stageIdParam={selectedStage}
          />
        </TabsContent>
      </Tabs>

      {/* Modal de Historial de Predicciones */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="bg-[#111A30] border-[#F4E39E]/30 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="user-history-modal">
          <DialogHeader>
            <DialogTitle className="gold-text" style={{ fontFamily: 'Montserrat' }}>
              Historial de {historyContext.user}
            </DialogTitle>
            <div className="text-white/50 text-xs">Vista: {historyContext.scopeLabel}</div>
          </DialogHeader>

          {historyLoading && (
            <div className="text-center text-white/50 py-10">Cargando historial...</div>
          )}

          {!historyLoading && historyError && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-300 text-sm">
              {historyError}
            </div>
          )}

          {!historyLoading && !historyError && historyData && (
            <div className="space-y-4">
              {/* Resumen */}
              <div className="grid grid-cols-2 gap-3">
                <div className="glass-card p-3 text-center">
                  <div className="text-white/50 text-xs uppercase tracking-wider">Partidos jugados</div>
                  <div className="text-2xl font-black text-white mt-1" style={{ fontFamily: 'Montserrat' }}>
                    {historyData.matches_played}
                  </div>
                </div>
                <div className="glass-card p-3 text-center bg-gradient-to-r from-[#F4E39E]/10 to-[#926F39]/10 border border-[#F4E39E]/40">
                  <div className="text-[#F4E39E]/80 text-xs uppercase tracking-wider">Puntos totales</div>
                  <div className="text-2xl font-black text-[#F4E39E] mt-1" style={{ fontFamily: 'Montserrat' }}>
                    {historyData.total_points} <span className="text-sm text-white/50">pts</span>
                  </div>
                </div>
              </div>

              {/* Lista de partidos */}
              {historyData.history.length === 0 ? (
                <div className="glass-card p-6 text-center text-white/40 text-sm">
                  Este usuario no tiene predicciones en partidos finalizados para esta vista.
                </div>
              ) : (
                <div className="space-y-2" data-testid="history-list">
                  {historyData.history.map((h, idx) => {
                    const exact = h.points === 3;
                    const correct = h.points === 1;
                    const wrong = h.points === 0;
                    return (
                      <div key={idx} className="glass-card p-3" data-testid={`history-item-${idx}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[#F4E39E] text-xs font-semibold">{h.match.stage.name}</span>
                          <span className="text-white/40 text-xs">{h.match.date} {h.match.time}</span>
                        </div>

                        {/* Equipos */}
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
                            <span className="text-white font-bold text-sm truncate">{h.match.home_team.code}</span>
                            <img src={FLAG_URL(h.match.home_team.flag_code)} alt="" className="w-7 h-5 object-cover rounded" onError={(e) => e.target.style.display = 'none'} />
                          </div>
                          <span className="gold-text font-black text-sm" style={{ fontFamily: 'Montserrat' }}>VS</span>
                          <div className="flex items-center gap-2 flex-1 justify-start min-w-0">
                            <img src={FLAG_URL(h.match.away_team.flag_code)} alt="" className="w-7 h-5 object-cover rounded" onError={(e) => e.target.style.display = 'none'} />
                            <span className="text-white font-bold text-sm truncate">{h.match.away_team.code}</span>
                          </div>
                        </div>

                        {/* Marcadores */}
                        <div className="grid grid-cols-3 gap-2 items-center text-center">
                          <div className="bg-[#0A0F1C]/60 rounded-lg p-2 border border-white/10">
                            <div className="text-white/50 text-[10px] uppercase tracking-wider">Predicción</div>
                            <div className="text-white font-black text-base" style={{ fontFamily: 'Montserrat' }}>
                              {h.prediction.home_goals} - {h.prediction.away_goals}
                            </div>
                          </div>
                          <div className="bg-[#0A0F1C]/60 rounded-lg p-2 border border-[#F4E39E]/30">
                            <div className="text-[#F4E39E]/80 text-[10px] uppercase tracking-wider">Resultado</div>
                            <div className="text-[#F4E39E] font-black text-base" style={{ fontFamily: 'Montserrat' }}>
                              {h.result.home_goals} - {h.result.away_goals}
                            </div>
                          </div>
                          <div className={`rounded-lg p-2 border ${exact ? 'bg-green-500/10 border-green-500/50' : correct ? 'bg-yellow-500/10 border-yellow-500/40' : 'bg-red-500/5 border-red-500/30'}`}>
                            <div className={`text-[10px] uppercase tracking-wider ${exact ? 'text-green-400' : correct ? 'text-yellow-400' : 'text-red-400/80'}`}>
                              Puntos
                            </div>
                            <div className={`font-black text-base ${exact ? 'text-green-300' : correct ? 'text-yellow-300' : 'text-red-300/80'}`} style={{ fontFamily: 'Montserrat' }}>
                              {h.points}
                              {exact && <span className="text-[10px] ml-1">EXACTO</span>}
                              {correct && <span className="text-[10px] ml-1">OK</span>}
                              {wrong && <span className="text-[10px] ml-1"></span>}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
